import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../../contexts'
import {
  OrderResponse,
  OrderResponseItem,
  PatchRequest,
  WebEdiDto,
} from '../../../../../../data/models'
import { useSelectFirstAndOnlyOption } from '../../../../../../hooks'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import {
  requestPatchOrderById,
  requestPutOrderItemById,
  useOrderItems,
} from '../../../../data'

interface Props {
  visible: boolean
  setVisible: (params: boolean) => void
  items: OrderResponseItem[] | null | undefined
  handleItems: (params: any) => void
  id: string
}

interface FormValues {
  confirmationReasonCode?: string
  confirmationReasonDescription?: string
}

type FormFieldName = 'confirmationReasonCode' | 'confirmationReasonDescription'

export const Reject: React.FC<Props> = ({
  visible,
  setVisible,
  items,
  handleItems,
  id,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  const { data: dataItems } = useOrderItems(id)
  // const { mutate: globalMutate } = useSWRConfig()

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  // ************************************************************
  // Formik
  // ************************************************************
  // notesCode and notesText:
  // if products is defined check if it is only one item than show the code
  // but if there are more products show empty string because maybe they
  // all have different reasons for rejections
  // const notesCode = () => {
  //   if (products) {
  //     return products.length === 1 ? products[0]?.Notes?.Code : undefined
  //   }
  //   return undefined
  // }
  // const notesText = () => {
  //   if (products) {
  //     return products.length === 1 ? products[0]?.Notes?.Text : ''
  //   }
  //   return ''
  // }

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationReasonCode: undefined,
      confirmationReasonDescription: '',
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationReasonCode',
          config: config.orderResponse.rejectOrderItem.confirmationReasonCode,
        },
        {
          fieldName: 'confirmationReasonDescription',
          config:
            config.orderResponse.rejectOrderItem.confirmationReasonDescription,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async formData => {
      // TODO: Einheitliches onSubmit überall erreichen?
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'orderResponse.positions.table.reject.dialog.save.messages.success.summary',
          ),
          detail: t(
            'orderResponse.positions.table.reject.dialog.save.messages.success.detail',
            {
              count: items?.length,
            },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'orderResponse.positions.table.reject.dialog.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  const handleClose = () => {
    setVisible(false)
    formik.resetForm()
    handleItems([]) // deselect the items in the table
  }

  const submitForm = async (formData: FormValues) => {
    const updatedItems = [...(dataItems as OrderResponseItem[])]

    items?.forEach(async item => {
      // make a copy of the item
      const newOrderResponseItem = produce(item, draft => {
        draft.ConfirmationCode = 'REJECTED'
        draft.ConfirmationReason!.Code = formData.confirmationReasonCode
        draft.ConfirmationReason!.Description =
          formData.confirmationReasonDescription
      })

      const itemIndex = updatedItems.findIndex(
        i => i.Id === newOrderResponseItem.Id,
      )

      if (itemIndex >= 0) {
        updatedItems[itemIndex] = newOrderResponseItem
      } else {
        updatedItems.push(newOrderResponseItem)
      }

      // patch for Payload.ConfirmationCode
      const confirmationCode: OrderResponse['ConfirmationCode'] = 'MODIFIED'
      const patchData: PatchRequest<WebEdiDto<OrderResponse>> = [
        {
          op: 'replace',
          path: '/Payload/ConfirmationCode',
          value: confirmationCode,
        },
      ]

      await requestPatchOrderById(id, patchData)
      await requestPutOrderItemById(id, item.Id, newOrderResponseItem)
    })
  }

  // ************************************************************
  // Components
  // ************************************************************
  // confirmationReasonCode
  const confirmationReasonCodeLabel = (
    <label
      htmlFor="confirmationReasonCode"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationReasonCode'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'confirmationReasonCode',
    config.orderResponse.rejectOrderItem.confirmationReasonCode.options,
    formik,
  )

  const confirmationReasonCodeComponent = (
    <Dropdown
      id="confirmationReasonCode"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationReasonCode'),
      })}
      value={formik.values.confirmationReasonCode}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const confirmationReasonCodeErrorMsg = (
    <div>{getFormErrorMessage('confirmationReasonCode')}</div>
  )

  const confirmationReasonCodeWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.rejectOrderItem}
      component={confirmationReasonCodeComponent}
      label={confirmationReasonCodeLabel}
      errorMsg={confirmationReasonCodeErrorMsg}
    />
  )

  // ConfirmationReasonDescription
  const confirmationReasonDescriptionLabel = (
    <label
      htmlFor="confirmationReasonDescription"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationReasonDescription'),
      })}
    ></label>
  )

  const confirmationReasonDescriptionComponent = (
    <InputText
      id="confirmationReasonDescription"
      value={formik.values.confirmationReasonDescription}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationReasonDescription'),
      })}
    />
  )

  const confirmationReasonDescriptionErrorMsg = (
    <div>{getFormErrorMessage('confirmationReasonDescription')}</div>
  )

  const ConfirmationReasonDescriptionWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.rejectOrderItem}
      component={confirmationReasonDescriptionComponent}
      label={confirmationReasonDescriptionLabel}
      errorMsg={confirmationReasonDescriptionErrorMsg}
    />
  )

  // header
  const header = (
    <>
      {t('orderResponse.positions.table.reject.dialog.title', {
        count: items?.length,
      })}
    </>
  )

  // footer
  const footer = (
    <>
      <Button
        label={t('orderResponse.positions.table.reject.dialog.cancel.label')}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t('orderResponse.positions.table.reject.dialog.save.label')}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={formik.submitForm}
        disabled={!formik.dirty}
        loading={isRequestRunning}
      />
    </>
  )

  return (
    <>
      <Dialog
        visible={visible}
        header={header}
        style={{ width: '35rem' }}
        breakpoints={{ [calculateBreakpointWidth(35)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          <div className="col-12">{confirmationReasonCodeWrapper}</div>

          <div className="col-12">{ConfirmationReasonDescriptionWrapper}</div>
        </div>
      </Dialog>
    </>
  )
}
